import SuperTokens from 'supertokens-auth-react';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import ThirdParty from 'supertokens-auth-react/recipe/thirdparty';
import Passwordless from 'supertokens-auth-react/recipe/passwordless';
import EmailVerification from 'supertokens-auth-react/recipe/emailverification';
import Session from 'supertokens-auth-react/recipe/session';
import type { AuthConfig } from './types';

const isLocalhost = (domain: string): boolean => {
  try {
    const hostname = new URL(domain).hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
  } catch {
    return true;
  }
};

const getHostname = (domain: string): string | undefined => {
  try {
    return new URL(domain).hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

const normalizeCookieDomain = (cookieDomain?: string): string | undefined => {
  if (!cookieDomain) {
    return undefined;
  }

  return cookieDomain.replace(/^\./, '').toLowerCase();
};

const hostnameMatchesCookieDomain = (hostname: string, cookieDomain: string): boolean => {
  return hostname === cookieDomain || hostname.endsWith(`.${cookieDomain}`);
};

const shouldUseSessionTokenFrontendDomain = (
  websiteDomain: string,
  apiDomain: string,
  cookieDomain?: string,
): boolean => {
  const normalizedCookieDomain = normalizeCookieDomain(cookieDomain);
  const websiteHostname = getHostname(websiteDomain);
  const apiHostname = getHostname(apiDomain);

  if (!normalizedCookieDomain || !websiteHostname || !apiHostname) {
    return false;
  }

  return (
    hostnameMatchesCookieDomain(websiteHostname, normalizedCookieDomain)
    && hostnameMatchesCookieDomain(apiHostname, normalizedCookieDomain)
  );
};

const REDIRECT_TO_STORAGE_KEY = 'om.auth.redirectTo';
const LOCALHOST_ORIGIN_REGEX = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i;
const REDIRECT_TO_MAX_AGE_MS = 10 * 60 * 1000;
const UI_ONLY_AUTH_QUERY_PARAMS = new Set([
  'error',
  'forceFreshAuth',
  'message',
  'prefillEmail',
  'show',
  'tenantId',
]);

let runtimeAuthRedirectUrl: string | undefined;
let runtimeWebsiteBasePath = '/auth';

type StoredRedirectToEntry = {
  value: string;
  createdAt?: number;
};

const normalizeWebsiteBasePath = (value?: string): string => {
  if (!value) {
    return '/auth';
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return '/auth';
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, '') || '/auth';
};

const isValidCurvatoOrigin = (value: string): boolean => {
  return /^https:\/\/([a-zA-Z0-9-]+\.)?curvato\.us$/i.test(value);
};

const isAllowedRedirectOrigin = (origin: string): boolean => {
  return isValidCurvatoOrigin(origin) || LOCALHOST_ORIGIN_REGEX.test(origin);
};

const normalizeRedirectTo = (value: string): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  try {
    const parsed = trimmed.startsWith('/')
      ? new URL(trimmed, window.location.origin)
      : new URL(trimmed);

    if (!isAllowedRedirectOrigin(parsed.origin)) {
      return undefined;
    }

    return parsed.toString();
  } catch {
    return undefined;
  }
};

const normalizeAuthRedirectUrl = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  try {
    const parsed = new URL(value);
    if (!isAllowedRedirectOrigin(parsed.origin)) {
      return undefined;
    }

    return parsed.origin;
  } catch {
    return undefined;
  }
};

const getRedirectToFromSearch = (search: string): string | undefined => {
  const params = new URLSearchParams(search);
  const redirectTo = params.get('redirectTo');
  const redirectToPath = params.get('redirectToPath');

  if (redirectTo) {
    return normalizeRedirectTo(redirectTo);
  }

  if (redirectToPath) {
    return normalizeRedirectTo(redirectToPath);
  }

  return undefined;
};

const hasAuthContinuationState = (search: string, hash: string): boolean => {
  if (hash.trim().length > 1) {
    return true;
  }

  const params = new URLSearchParams(search);
  for (const key of params.keys()) {
    if (!UI_ONLY_AUTH_QUERY_PARAMS.has(key)) {
      return true;
    }
  }

  return false;
};

const storeRedirectTo = (redirectTo: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const normalizedRedirectTo = normalizeRedirectTo(redirectTo);
  if (!normalizedRedirectTo) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      REDIRECT_TO_STORAGE_KEY,
      JSON.stringify({ value: normalizedRedirectTo, createdAt: Date.now() })
    );
  } catch {
    // Ignore storage write failures in restricted browser contexts.
  }
};

const getStoredRedirectToEntry = (): StoredRedirectToEntry | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const rawValue = window.sessionStorage.getItem(REDIRECT_TO_STORAGE_KEY);
    if (!rawValue) {
      return undefined;
    }

    try {
      const parsed = JSON.parse(rawValue) as StoredRedirectToEntry;
      const normalizedValue = typeof parsed?.value === 'string'
        ? normalizeRedirectTo(parsed.value)
        : undefined;

      if (!normalizedValue) {
        return undefined;
      }

      return {
        value: normalizedValue,
        createdAt: typeof parsed.createdAt === 'number' ? parsed.createdAt : undefined,
      };
    } catch {
      const normalizedValue = normalizeRedirectTo(rawValue);
      if (!normalizedValue) {
        return undefined;
      }

      return { value: normalizedValue };
    }
  } catch {
    // Ignore storage read failures.
  }

  return undefined;
};

const getStoredRedirectTo = (): string | undefined => {
  const entry = getStoredRedirectToEntry();
  if (!entry) {
    return undefined;
  }

  if (entry.createdAt !== undefined && Date.now() - entry.createdAt > REDIRECT_TO_MAX_AGE_MS) {
    clearStoredRedirectTo();
    return undefined;
  }

  return entry.value;

};

const clearStoredRedirectTo = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(REDIRECT_TO_STORAGE_KEY);
  } catch {
    // Ignore storage remove failures.
  }
};

const syncRedirectToStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const redirectTo = getRedirectToFromSearch(window.location.search);
  if (redirectTo) {
    storeRedirectTo(redirectTo);
    return;
  }

  const path = window.location.pathname;
  const normalizedWebsiteBasePath = normalizeWebsiteBasePath(runtimeWebsiteBasePath);
  const isAuthEntryPath =
    path === normalizedWebsiteBasePath
    || path.startsWith(`${normalizedWebsiteBasePath}/`);
  if (isAuthEntryPath && !hasAuthContinuationState(window.location.search, window.location.hash)) {
    clearStoredRedirectTo();
  }
};

const getAuthEntryBaseUrl = (): URL | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const websiteBasePath = normalizeWebsiteBasePath(runtimeWebsiteBasePath);
  const authOrigin = runtimeAuthRedirectUrl || window.location.origin;

  try {
    return new URL(websiteBasePath, authOrigin);
  } catch {
    return new URL(websiteBasePath, window.location.origin);
  }
};

export const getWebsiteBasePath = (): string => runtimeWebsiteBasePath;

export const buildAuthRedirectToUrl = (returnTo?: string): string => {
  if (typeof window === 'undefined') {
    return normalizeWebsiteBasePath(runtimeWebsiteBasePath);
  }

  const target = getAuthEntryBaseUrl();
  if (!target) {
    return normalizeWebsiteBasePath(runtimeWebsiteBasePath);
  }

  const normalizedReturnTo = normalizeRedirectTo(returnTo ?? window.location.href);
  if (normalizedReturnTo) {
    target.searchParams.set('redirectTo', normalizedReturnTo);
  }

  return target.toString();
};

export const replaceWindowLocation = (url: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.location.replace(url);
};

export const consumePendingCrossOriginAuthRedirect = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const redirectTo = getValidRedirectTo();
  if (!redirectTo) {
    return undefined;
  }

  try {
    const target = new URL(redirectTo);
    if (target.origin === window.location.origin) {
      return undefined;
    }

    clearStoredRedirectTo();
    return target.toString();
  } catch {
    return undefined;
  }
};

/**
 * Reads redirect target from the current URL and validates it belongs to
 * `*.curvato.us` in production (or localhost in local development).
 */
const getValidRedirectTo = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const redirectTo = getRedirectToFromSearch(window.location.search);
  if (redirectTo) {
    storeRedirectTo(redirectTo);
    return redirectTo;
  }

  return getStoredRedirectTo();
};

/**
 * Initialize SuperTokens with standardized config across all apps.
 * Enforces full recipe suite and SSO-ready cookie config in production.
 */
export function initAuth(config: AuthConfig): void {
  const {
    appName,
    websiteDomain,
    apiDomain,
    apiBasePath = '/auth',
    websiteBasePath = '/auth',
    successRedirectUrl = '/',
    publicRoutes = ['/', '/auth'],
    isProduction = false,
    cookieDomain,
    authRedirectUrl,
    requireAuthRedirectUrl = false,
  } = config;

  const emailVerificationMode =
    config.emailVerificationMode ?? (isProduction ? 'REQUIRED' : 'OPTIONAL');

  runtimeWebsiteBasePath = normalizeWebsiteBasePath(websiteBasePath);
  runtimeAuthRedirectUrl = normalizeAuthRedirectUrl(authRedirectUrl);

  if (requireAuthRedirectUrl && !runtimeAuthRedirectUrl) {
    throw new Error(
      'Missing required authRedirectUrl. Set VITE_AUTH_URL to the centralized auth portal URL.'
    );
  }

  // Use cookie-based sessions in production for cross-subdomain SSO,
  // header-based in local dev (cookies don't work cross-port on localhost)
  const usesCookies = !isLocalhost(websiteDomain);
  const useSessionTokenFrontendDomain = usesCookies && shouldUseSessionTokenFrontendDomain(
    websiteDomain,
    apiDomain,
    cookieDomain,
  );

  syncRedirectToStorage();

  SuperTokens.init({
    appInfo: {
      appName,
      apiDomain,
      websiteDomain,
      apiBasePath,
      websiteBasePath: runtimeWebsiteBasePath,
    },
    recipeList: [
      EmailPassword.init({
        signInAndUpFeature: {
          signUpForm: {
            formFields: [
              {
                id: 'email',
                label: 'Email',
                placeholder: 'name@example.com',
                getDefaultValue: () => {
                  if (typeof window === 'undefined') return '';
                  const params = new URLSearchParams(window.location.search);
                  return params.get('prefillEmail') || '';
                },
              },
              {
                id: 'username',
                label: 'Username',
                placeholder: 'jane_doe',
                validate: async (value) => {
                  if (value.length < 3) return 'Username must be at least 3 characters';
                  return undefined;
                },
              },
              {
                id: 'full_name',
                label: 'Full name',
                placeholder: 'Jane Doe',
                validate: async (value) => {
                  if (value.length < 1) return 'Full name is required';
                  return undefined;
                },
              },
            ],
          },
        },
        getRedirectionURL: async (context) => {
          if ((context.action as string) === 'SUCCESS' || (context.action as string) === 'ALREADY_SIGNED_IN') {
            const redirectTo = getValidRedirectTo();
            clearStoredRedirectTo();
            return redirectTo ?? successRedirectUrl;
          }
          return undefined;
        },
      }),
      ThirdParty.init({
        signInAndUpFeature: {
          providers: [
            ThirdParty.Google.init(),
          ],
        },
        getRedirectionURL: async (context: { action: string }) => {
          if (context.action === 'SUCCESS' || context.action === 'ALREADY_SIGNED_IN') {
            const redirectTo = getValidRedirectTo();
            clearStoredRedirectTo();
            return redirectTo ?? successRedirectUrl;
          }
          return undefined;
        },
      }),
      Passwordless.init({
        contactMethod: 'EMAIL',
        getRedirectionURL: async (context: { action: string }) => {
          if (context.action === 'SUCCESS' || context.action === 'ALREADY_SIGNED_IN') {
            const redirectTo = getValidRedirectTo();
            clearStoredRedirectTo();
            return redirectTo ?? successRedirectUrl;
          }
          return undefined;
        },
      }),
      EmailVerification.init({ mode: emailVerificationMode }),
      Session.init({
        tokenTransferMethod: usesCookies ? 'cookie' : 'header',
        ...(useSessionTokenFrontendDomain && cookieDomain
          ? { sessionTokenFrontendDomain: cookieDomain }
          : {}),
        autoAddCredentials: true,
        maxRetryAttemptsForSessionRefresh: 1,
        onHandleEvent: (event) => {
          if (event.action === 'UNAUTHORISED') {
            if (typeof window !== 'undefined') {
              const path = window.location.pathname;
              const isPublicRoute = publicRoutes.some(
                (route) => path === route || path.startsWith(route + '/')
              );
              if (isPublicRoute) return;

              try {
                Session.signOut();
              } catch {
                // Ignore sign-out errors after session expiry
              }
              const normalizedWebsiteBasePath = normalizeWebsiteBasePath(runtimeWebsiteBasePath);
              const isAuthPath =
                path === normalizedWebsiteBasePath
                || path.startsWith(`${normalizedWebsiteBasePath}/`);

              if (!isAuthPath) {
                replaceWindowLocation(buildAuthRedirectToUrl(window.location.href));
              }
            }
          }
        },
      }),
    ],
  });
}
